import { Divider, Flex, Tooltip } from '@byecode/ui'
import { type DataSourceAbstract, type Field, type SchemaProtocol } from '@lighthouse/core'
import { getViewColumns, Group4ByecodeUi, systemPersonFieldId } from '@lighthouse/shared'
import React, { useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { EventList } from '@/components/EventList'
import { PopoverQuestion } from '@/components/PopoverQuestion'
import { ActionAdderProvider } from '@/contexts/ActionAdderContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'

import { ButtonSettingList } from '../../Button/ButtonSettingList'
import { QuickFilterSetting } from '../../Common/QuickFilterSetting'
import { AllowExportSetting, AllowFieldSetting, AllowImportSetting, AllowPrintSetting, SwitchItem } from '../../Common/UserOperate'

interface KanbanOperateProps {
    id: string
    dataSource: DataSourceAbstract
    isUserDataSource?: boolean
}

export const KanbanOperate: React.FC<KanbanOperateProps> = ({ id, dataSource, isUserDataSource }) => {
    const { watch, control, setValue } = useFormContext()
    const [
        viewType,
        kanbanGroupConfigure,
        viewFieldSettings,
        dsId,
        canImport,
        canPrint,
        canExport,
        userImportSetting,
        triggerWorkflow,
        canSort,
        userSortSetting
    ] = watch([
        'viewType',
        'kanbanGroupConfigure',
        'viewFieldSettings',
        'pointer',
        'canImport',
        'canPrint',
        'canExport',
        'userImportSetting',
        'triggerWorkflow',
        'canSort',
        'userSortSetting'
    ])
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const { groupByFieldId } = kanbanGroupConfigure || {}
    const { schema, viewOptions, type } = dataSource
    const { tableProps } = viewOptions
    const isJoinDataSource = false /* useMemo(() => type === DataSourceType.joinDataSource, [type]) */
    const columns = useMemo(
        () =>
            getViewColumns({
                tableProps,
                value: viewFieldSettings,
                schema
            }),
        [schema, tableProps, viewFieldSettings]
    )

    // 合并数据源与视图上的字段
    const mergedSchema = useMemo(() => {
        return columns.reduce<SchemaProtocol['schema']>((acc, item) => {
            acc[item.fieldId] = { ...item, id: item.fieldId, name: item.title } as Field
            return acc
        }, {})
    }, [columns])

    const disableDragKanban =
        !!groupByFieldId && schema[groupByFieldId]?.type === 'person' && systemPersonFieldId.has(schema[groupByFieldId].id)

    return (
        <>
            <Group4ByecodeUi
                label={
                    <PopoverQuestion
                        type="toolOperation"
                        arrowOffset={20}
                        renderTarget={(node, ref) => (
                            <Flex gap={4} alignItems="center" ref={ref}>
                                工具栏操作{node}
                            </Flex>
                        )}
                    />
                }
            >
                <div style={{ padding: '0 8px' }}>
                    <ButtonSettingList prefixName="toolbar" hideConfigs={{ fillWay: true }} disableViewOption />
                </div>
            </Group4ByecodeUi>

            <Divider color="var(--color-gray-200)" />

            <Group4ByecodeUi label="行操作">
                <div style={{ padding: '0 8px' }}>
                    <ActionAdderProvider viewDsId={dsId} viewType={viewType}>
                        <ButtonSettingList prefixName="recordOperator" hideConfigs={{ fillWay: true }} />
                    </ActionAdderProvider>
                </div>
            </Group4ByecodeUi>

            <Divider color="var(--color-gray-200)" />

            <Controller
                control={control}
                name="eventList"
                render={({ field }) => (
                    <EventList blockType="view" view={{ viewType, viewDsId: dsId }} value={field.value} onChange={field.onChange} />
                )}
            />
            <Divider color="var(--color-gray-200)" />

            <Group4ByecodeUi label="其它操作">
                <Controller
                    control={control}
                    name="canCreateRecord"
                    render={({ field: { value, onChange } }) => (
                        <SwitchItem icon="KanbanAdd" label="分组下添加记录" value={value} onChange={onChange} />
                    )}
                />
                {!isJoinDataSource && (
                    <AllowImportSetting
                        type="UploadSimple"
                        title="批量导入"
                        columns={columns}
                        value={{ canImport, userImportSetting, triggerWorkflow }}
                        dataSource={dataSource}
                        dataSourceList={dataSourceList}
                        onChange={val => {
                            const { canImport, userImportSetting, triggerWorkflow } = val
                            setValue('canImport', canImport)
                            setValue('userImportSetting', userImportSetting)
                            setValue('triggerWorkflow', triggerWorkflow)
                        }}
                    />
                )}
                <AllowPrintSetting
                    type="Print"
                    title="打印"
                    dsId={dsId}
                    value={{ canPrint }}
                    onChange={val => {
                        const { canPrint } = val
                        setValue('canPrint', canPrint)
                    }}
                />
                <AllowExportSetting
                    type="ImportExcel"
                    title="导出"
                    dsId={dsId}
                    columns={columns}
                    dataSource={dataSource}
                    dataSourceList={dataSourceList}
                    value={{ canExport }}
                    onChange={val => {
                        const { canExport } = val
                        setValue('canExport', canExport)
                    }}
                />

                <Controller
                    control={control}
                    name="canSetKanban"
                    render={({ field: { value, onChange } }) => (
                        <SwitchItem icon="Columns" label="设置看板" value={value} onChange={onChange} />
                    )}
                />
                <Controller
                    control={control}
                    name="canSortKanban"
                    render={({ field: { value, onChange } }) =>
                        disableDragKanban ? (
                            <Tooltip title="在看板分组依据选择的字段，不支持编辑">
                                <SwitchItem
                                    disabled={disableDragKanban}
                                    icon="DragCard"
                                    label="拖拽卡片"
                                    value={value}
                                    onChange={onChange}
                                />
                            </Tooltip>
                        ) : (
                            <SwitchItem disabled={disableDragKanban} icon="DragCard" label="拖拽卡片" value={value} onChange={onChange} />
                        )
                    }
                />
                <Controller
                    control={control}
                    name="canSearch"
                    render={({ field }) => <SwitchItem icon="SearchLine" label="搜索数据" value={field.value} onChange={field.onChange} />}
                />
                <Controller
                    control={control}
                    name="canDisplay"
                    render={({ field }) => <SwitchItem icon="Setting" label="设置字段" value={field.value} onChange={field.onChange} />}
                />
                <QuickFilterSetting schema={mergedSchema} dataSourceList={dataSourceList} columns={columns} />
                <AllowFieldSetting
                    type="ArrowsDownUp"
                    title="使用排序"
                    columns={columns}
                    value={{ canSort, userSortSetting }}
                    onChange={val => {
                        const { canSort, userSortSetting } = val
                        setValue('canSort', canSort)
                        setValue('userSortSetting', userSortSetting)
                    }}
                />

                <Controller
                    control={control}
                    name="canGroup"
                    render={({ field }) => <SwitchItem icon="GroupBy" label="设置分组" value={field.value} onChange={field.onChange} />}
                />
            </Group4ByecodeUi>
        </>
    )
}
